import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";
import Button from "@components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useMediaStore } from "@/store";
import { getExtractedFrameIdsForMediaItem } from "@/hooks/useMediaUtils";
import { MediaItemStatus } from "@/types/media";
import { useMemo } from "react";
import { useMedia } from "@/contexts/MediaContext";

interface GeneratePredictionsBtnProps {
    disabled?: boolean;
    onClick: (e: React.MouseEvent) => void;
}

export const GeneratePredictionsBtn = ({
    disabled = false,
    onClick
}: GeneratePredictionsBtnProps) => {
    const isGeneratingPredictions = useIsGeneratingPredictions();
    const { pendingMediaItems, mediaItems } = useMedia();
    const media = useMediaStore();

    // Check if files are still being uploaded/processed
    const hasFilesUploading = useMemo(() => {
        return (
            pendingMediaItems.length > 0 ||
            mediaItems.some((item) => {
                const extractedFrameIds = getExtractedFrameIdsForMediaItem(item);
                const uploadingExtractedFrames = extractedFrameIds.some(
                    (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
                );

                return (
                    item.status === MediaItemStatus.UPLOADING ||
                    item.status === MediaItemStatus.PROCESSING ||
                    uploadingExtractedFrames
                );
            })
        );
    }, [media, pendingMediaItems, mediaItems]);

    const isLoading = isGeneratingPredictions || hasFilesUploading;

    return (
        <Button
            type="tertiary"
            aria-label="Find Objects"
            title="Find Objects"
            onClick={onClick}
            disabled={disabled || isLoading}
            className="border-brand"
            loadingText={hasFilesUploading ? "Processing Files..." : "Finding objects..."}
            loading={isLoading}
        >
            Find Objects
            <div className={twClassNames("hotkeysContainer", disabled && "opacity-50")}>
                <div className="hotkey brand">⌘</div>
                <div className="hotkey brand">⏎</div>
            </div>
        </Button>
    );
};
